import React, { useEffect, useState } from "react";
import { getUserData, updateUserField } from "../firebase/firestoreService";
import LoadingPage from "./LoadingPage";
import { useNavigate } from "react-router-dom";

const npkStyle = {
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    overflow: "hidden",
    fontFamily: "'Poppins', sans-serif",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backdropFilter: "blur(5px)", // Subtle background blur
  },
  formContainer: {
    background: "rgba(255, 255, 255, 0.3)", // Less transparency
    backdropFilter: "blur(10px)", // Slight blur
    padding: "15px 30px", // Reduced padding
    borderRadius: "15px", // Smaller border-radius for compactness
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)", // Slightly smaller shadow
    textAlign: "center",
    maxWidth: "420px", // Adjusted width to fit the label/input
    width: "80%",
    border: "2px solid #f1f8e9",
  },
  h3: {
    fontFamily: "'Roboto Slab', serif",
    fontSize: "1.6rem", // Slightly smaller font size
    padding: "10px",
    color: "#FFFFFF",
    fontWeight: "bold",
    textShadow: "1px 1px 3px rgba(0, 0, 0, 0.4)",
  },
  label: {
    fontSize: "14px",
    color: "#FFFFFF",
    marginBottom: "6px",
    display: "block", // To make the label appear above the input
    textAlign: "left", // Align label text to the left
  },
  inputWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "15px", // Space between the input sections
  },
  input: {
    width: "60%", // Input takes up 60% of the container width
    padding: "10px", // Reduced padding
    marginLeft: "10px", // Slight margin between label and input
    borderRadius: "8px", // Smaller border-radius
    border: "1px solid #FFFFFF",
    fontSize: "14px",
    backgroundColor: "#F1F8E9",
    boxSizing: "border-box",
    transition: "all 0.3s ease",
    outline: "none",
    color: "#333",
  },
  inputFocus: {
    borderColor: "#66bb6a",
    boxShadow: "0 0 6px rgba(102, 187, 106, 0.6)", // Subtle focus glow
  },
  button: {
    width: "100%",
    padding: "12px", // Reduced padding
    margin: "10px 0", // Slight margin reduction
    borderRadius: "8px", // Smaller border-radius
    border: "none",
    fontSize: "14px", // Slightly smaller font size
    cursor: "pointer",
    backgroundColor: "#66bb6a",
    color: "white",
    fontWeight: "bold",
    transition: "background-color 0.3s ease, transform 0.3s ease, box-shadow 0.2s ease",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
  },
  buttonHover: {
    backgroundColor: "#f1f8e9",
    color: "#66bb6a", // Change text color when hovered
    transform: "scale(1.0)", // Slightly scale the button on hover
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.3)", // Increase shadow on hover
  },
  errorText: {
    color: "red",
    fontSize: "12px",
    marginTop: "8px",
  },
};

const NPKComponent = ({ email }) => {
  const [npk, setNpk] = useState([0, 0, 0]);
  const [inputFocus, setInputFocus] = useState(false);
  const [inputHover, setInputHover] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);
  const [loading, setLoading] = useState(true); // Track loading state
  const [updating, setUpdating] = useState(false); // Track update state

  const navigate = useNavigate();


  useEffect(() => {
    const fetchData = async () => {
      const userData = await getUserData(email);
      if (userData?.npk) {
        setNpk(userData.npk);
      }
      setLoading(false);
    };
    fetchData();
  }, [email]);

  const handleUpdate = async () => {
    setUpdating(true); // Set updating to true to disable the button
    await updateUserField(email, "npk", npk); // Update NPK in the database
    alert("NPK updated successfully!");
    setUpdating(false); // Set updating to false after update

    if (window.history.length > 2) {
      navigate(-1); // Go back to the previous page
    } else {
      navigate('/dashboard'); // No history, go to dashboard
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div style={npkStyle.container}>
      <div style={npkStyle.formContainer}>
        <h3 style={npkStyle.h3}>Update NPK Values</h3>

        <div style={npkStyle.inputWrapper}>
          {/* Label and input for Nitrogen */}
          <label style={npkStyle.label} htmlFor="nitrogen">Nitrogen (N)</label>
          <input
            id="nitrogen"
            style={inputHover ? { ...npkStyle.input, ...npkStyle.inputFocus } : npkStyle.input}
            type="number"
            value={npk[0]}
            onChange={(e) => setNpk([+e.target.value, npk[1], npk[2]])}
            onFocus={() => setInputFocus(true)}
            onBlur={() => setInputFocus(false)}
            onMouseEnter={() => setInputHover(true)}
            onMouseLeave={() => setInputHover(false)}
          />
        </div>

        <div style={npkStyle.inputWrapper}>
          {/* Label and input for Phosphorus */}
          <label style={npkStyle.label} htmlFor="phosphorus">Phosphorus (P)</label>
          <input
            id="phosphorus"
            style={inputHover ? { ...npkStyle.input, ...npkStyle.inputFocus } : npkStyle.input}
            type="number"
            value={npk[1]}
            onChange={(e) => setNpk([npk[0], +e.target.value, npk[2]])}
            onFocus={() => setInputFocus(true)}
            onBlur={() => setInputFocus(false)}
            onMouseEnter={() => setInputHover(true)}
            onMouseLeave={() => setInputHover(false)}
          />
        </div>

        <div style={npkStyle.inputWrapper}>
          {/* Label and input for Potassium */}
          <label style={npkStyle.label} htmlFor="potassium">Potassium (K)</label>
          <input
            id="potassium"
            style={inputHover ? { ...npkStyle.input, ...npkStyle.inputFocus } : npkStyle.input}
            type="number"
            value={npk[2]}
            onChange={(e) => setNpk([npk[0], npk[1], +e.target.value])}
            onFocus={() => setInputFocus(true)}
            onBlur={() => setInputFocus(false)}
            onMouseEnter={() => setInputHover(true)}
            onMouseLeave={() => setInputHover(false)}
          />
        </div>

        <button
          style={buttonHover ? { ...npkStyle.button, ...npkStyle.buttonHover } : npkStyle.button}
          onClick={handleUpdate}
          onMouseEnter={() => setButtonHover(true)}
          onMouseLeave={() => setButtonHover(false)}
          disabled={updating} // Disable button when updating
        >
          {updating ? "Saving..." : "Update NPK"} {/* Change button text based on updating state */}
        </button>
      </div>
    </div>
  );
};

export default NPKComponent;
